import {
  type PromotionMetadata,
  type ProductCampaignsPromotion,
} from '@/features/shop/services/Product/types'
import { type ProductCardProduct } from '@/features/shop/components/ProductCard/types'
import { getPromotionName } from '@/features/shop/components/ProductCard/Utils'
import { TransparentButton } from '@/features/shared/elements/Buttons'
import { Body } from '@shipt/design-system-typography'
import { ChevronRightIcon } from '@shipt/design-system-icons'
import styled from 'styled-components'
import { useSegwayRouter } from '@/features/shared/hooks/useSegwayRouter'
import { getRetailerFeaturedPromotionsUrl } from '@shared/constants/routes'
import { useUserStoreName } from '@/features/account/services/User/hooks'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { DynamicFreeItemPromoModal } from '@/features/shop/components/Modal/FreeItemPromoModal/DynamicFreeItemPromoModal'

interface Props {
  cpgPromotion: ProductCampaignsPromotion | null | undefined
  isVertical?: boolean
  isPromotionCompleted?: boolean
  promotionMetadata?: PromotionMetadata | null
  selectedFreeItem?: ProductCardProduct
}

export const ProductCardClickablePromotion = ({
  cpgPromotion,
  isVertical = false,
  isPromotionCompleted = false,
  promotionMetadata,
  selectedFreeItem,
}: Props) => {
  const promotionText = getPromotionName(
    cpgPromotion,
    false,
    isPromotionCompleted,
    promotionMetadata,
  )
  const router = useSegwayRouter()
  const storeName = useUserStoreName()

  const labelCategoryId = promotionMetadata?.label_category_id
  const promotionId = promotionMetadata?.promotion_ids?.[0] ?? cpgPromotion?.id
  const { openDialog } = useHandleDialog()

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (labelCategoryId !== undefined) {
      openDialog(DynamicFreeItemPromoModal, {
        labelCategoryId,
        promotionId: promotionId?.toString(),
        selectedFreeItem,
        modalMetadata: promotionMetadata?.modal_metadata,
      })
      return
    }

    if (!cpgPromotion) {
      return
    }

    router.push(
      `${getRetailerFeaturedPromotionsUrl(
        storeName,
        cpgPromotion.category_id,
      )}?promotion_category_id=${cpgPromotion.category_id}`,
    )
  }

  return (
    <ClickablePromotionButton
      isVertical={isVertical}
      onClick={handleClick}
      aria-label={`${promotionText}, view promotion details`}
    >
      <PromotionText
        size={isVertical ? 'md' : 'sm'}
        as="span"
        concept="feedback"
        feedbackType="promotion"
        strong
        lineHeight={isVertical ? 'default' : 'compact'}
      >
        {promotionText}
        <ChevronIcon
          $isVertical={isVertical}
          size={isVertical ? 'md' : 'sm'}
          concept="feedback"
          feedbackType="promotion"
        />
      </PromotionText>
    </ClickablePromotionButton>
  )
}

const ClickablePromotionButton = styled(TransparentButton)<{
  isVertical: boolean
}>`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  text-align: left;
  padding: ${({ isVertical }) => (isVertical ? '0' : '0.375rem 0')};
`

const PromotionText = styled(Body)`
  @supports (text-wrap: pretty) {
    text-wrap: pretty;
  }

  @supports not (text-wrap: pretty) {
    text-wrap: balance;
  }
`

const ChevronIcon = styled(ChevronRightIcon)<{ $isVertical: boolean }>`
  vertical-align: ${({ $isVertical }) => ($isVertical ? 'top' : 'text-bottom')};
  margin-left: 0.125rem;
`
